import React from "react";
import type { ApolloClient } from "@apollo/client";
import { connect, type ConnectedProps } from "react-redux";
import { withApollo, type WithApolloClient } from "@apollo/client/react/hoc";
import { isEmpty, get, capitalize, some } from "lodash";
import { Card, OverlayTrigger, Popover, Row, Col } from "react-bootstrap";
import { FiAlertTriangle, FiTrash2, FiUsers } from "react-icons/fi";
import { BsDot } from "react-icons/bs";
import { push } from "store/actions/router";

import IconCircle from "components/IconCircle";
import IconHeader from "components/IconHeader";
import Button from "components/Button";
import Alert from "components/Alert";

import {
  contributionConstants,
  bankConstants,
  payrollConstants,
} from "actions/types";
import { deleteBank, deleteEmployerBank } from "actions/bankActions";
import { createLoadingSelector, createErrorSelector } from "store/selectors";
import { addContributionSource } from "actions/contributionActions";
import { toast } from "react-toastify";
import { isEmployerSelector } from "store/selectors/user";
import { wrapError } from "utils/errorHelper";
import { getIraAccountIdSelector } from "store/selectors/user";
import {
  getPayrollHistory,
  cancelPayrollsByBankId,
} from "actions/payrollActions";
import SimpleModal from "components/SimpleModal";
import type { UserBank } from "types/graphqlSchema";
import type { RootState } from "types/state";

import "./BankCard.scss";

// Shape of the payroll history records read off the GraphQL response via
// lodash `get(data, "data.payrollHistory", [])`.
interface PayrollHistoryItem {
  status?: string;
  bankId?: string;
}

const isUpdatingSelector = createLoadingSelector([
  contributionConstants.CONTRIBUTION,
  payrollConstants.GET_PAYROLL_HISTORY,
  payrollConstants.CANCEL_PAYROLLS,
]);

const errorSelector = createErrorSelector([
  contributionConstants.CONTRIBUTION,
  bankConstants.DELETE_BANK,
]);

const mapStateToProps = (state: RootState) => {
  return {
    isUpdating: isUpdatingSelector(state),
    error: errorSelector(state),
    // narrow away `undefined` from the shallow-typed selector
    // (getIraAccountIdSelector returns string | null | undefined)
    iraAccountId: getIraAccountIdSelector(state) as string | null,
    isEmployer: isEmployerSelector(state),
  };
};

const mapDispatchToProps = {
  push,
  deleteBank,
  addContributionSource,
  deleteEmployerBank,
  getPayrollHistory,
  cancelPayrollsByBankId,
};

const connector = connect(mapStateToProps, mapDispatchToProps);

type PropsFromRedux = ConnectedProps<typeof connector>;

interface OwnProps {
  // Consumed by employer BankAccounts.tsx, which passes a UserBank here.
  bankAccount: UserBank;
  contributionAbility?: object;
  allowContribution?: boolean;
}

type Props = WithApolloClient<OwnProps & PropsFromRedux>;

interface State {
  isDeleting: boolean;
  isContributing: boolean;
  contributionAmount: number;
  applyToPriorYear: boolean;
  error: string;
  isLoading: boolean;
  showApprovedModal: boolean;
  showProcessingModal: boolean;
}

class BankCard extends React.PureComponent<Props, State> {
  static defaultProps = {
    allowContribution: true,
  };

  _removeInFlight = false;
  _exited = false;

  constructor(props: Props) {
    super(props);

    this.state = {
      isDeleting: false,
      isContributing: false,
      contributionAmount: 1,
      applyToPriorYear: false,
      error: "",
      isLoading: false,
      showApprovedModal: false,
      showProcessingModal: false,
    };
  }

  _removeBankLink = () => {
    if (this._removeInFlight) {
      return;
    }
    this._removeInFlight = true;
    this._exited = false;
    this.setState({
      isLoading: true,
    });
    const bankId = this.props.bankAccount.id;
    if (this.props.isEmployer) {
      void this.props
        .getPayrollHistory(this.props.client as ApolloClient<object>)
        .then((data) => {
          if (this._exited) {
            return Promise.resolve();
          }
          // @ts-expect-error data is the dispatched action union; the inferred
          // type narrows to the SUCCESS action, but the FAILURE action carries
          // `error` at runtime — preserved guard from the original JS.
          if (data && data.error) {
            // @ts-expect-error see note above — `error` read off the FAILURE
            // action's runtime shape; preserved from the original JS.
            this.setState({ error: wrapError(data.error), isLoading: false });
            return Promise.resolve();
          }
          const payrollHistory = get(
            data,
            "data.payrollHistory",
            []
          ) as PayrollHistoryItem[];
          const approved = payrollHistory.filter(
            (payroll) => payroll.status === "APPROVED"
          );
          const processing = payrollHistory.filter(
            (payroll) => payroll.status === "PROCESSING"
          );
          if (this._isBankInList(approved, bankId)) {
            return this.setState({
              showApprovedModal: true,
            });
          } else if (this._isBankInList(processing, bankId)) {
            return this.setState({
              showProcessingModal: true,
            });
          } else {
            return this._deleteEmployerBank();
          }
        })
        .then(() => this.setState({ isLoading: false }))
        .finally(() => {
          this._removeInFlight = false;
        });

      return;
    }
    void this.props
      .deleteBank(this.props.client as ApolloClient<object>, bankId)
      .then(() => {
        if (!this.props.error) {
          toast.success("Successfully removed your bank account.");
          this.setState({
            isLoading: false,
          });
        }
      })
      .finally(() => {
        this._removeInFlight = false;
      });
  };

  _confirmApprovedPayrollRemoval = () => {
    if (this._removeInFlight) {
      return;
    }
    this._removeInFlight = true;
    void this._deleteEmployerBank(true).finally(() => {
      this._removeInFlight = false;
    });
  };

  _exitDeleteView = () => {
    this._exited = true;
    this.setState({ isDeleting: false });
  };

  _isBankInList = (payrolls: PayrollHistoryItem[], bankId: string) => {
    return some(payrolls, (payroll) => payroll.bankId === bankId);
  };

  _deleteEmployerBank = (cancelPayrolls = false) => {
    this.setState({
      isLoading: true,
      showApprovedModal: false,
    });
    const bankId = get(this.props, "bankAccount.id") as string;
    if (!cancelPayrolls) {
      return this.props
        .deleteEmployerBank(this.props.client as ApolloClient<object>, bankId)
        .then((data) => {
          // `data` is the dispatched action; the FAILURE action carries `error`
          // at runtime, so it is read off a widened view here — preserved guard
          // from the original JS.
          if (data && (data as { error?: unknown }).error) {
            this.setState({
              error: wrapError((data as { error?: unknown }).error),
              isLoading: false,
            });
          }
        });
    }
    return this.props
      .cancelPayrollsByBankId(this.props.client as ApolloClient<object>, bankId)
      .then((data) => {
        // See note above — read the runtime FAILURE `error` field off a
        // widened view of the dispatched action.
        if (data && (data as { error?: unknown }).error) {
          this.setState({
            error: wrapError((data as { error?: unknown }).error),
            isLoading: false,
          });
          // Propagate the cancel FAILURE action so the delete step is skipped
          // and the chain resolves to the failure, not undefined. The guard
          // above proved `error` is present, so narrow to the FAILURE-action
          // member for the .then union.
          return data as { type: string; error: string; success: false };
        }
        return this.props.deleteEmployerBank(
          this.props.client as ApolloClient<object>,
          bankId
        );
      })
      .then((data) => {
        // See note above — read the runtime FAILURE `error` field off a
        // widened view of the dispatched action.
        if (data && (data as { error?: unknown }).error) {
          this.setState({
            error: wrapError((data as { error?: unknown }).error),
            isLoading: false,
          });
        }
      });
  };

  _getErrorMessage = () => {
    if (!isEmpty(this.state.error)) {
      return this.state.error;
    }
    return this.props.error;
  };

  _getDisplayView() {
    const accountType = get(this.props.bankAccount, "accountSubtype");
    const accountId = get(this.props.bankAccount, "accountId");
    const bankAlias = get(this.props.bankAccount, "bankAlias");
    const bankName = get(this.props.bankAccount, "bankName");

    const popover = (
      <Popover className="action-popover">
        <Popover.Body>
          <ul className="bank-actions">
            <li
              className="bank-action-item"
              onClick={() => {
                this.props.push("/dashboard/company/group");
              }}
            >
              <FiUsers
                className="svg"
                color="white"
                stroke="#0a2540"
                size={14}
              />
              <p className="action-label">View Linked Groups</p>
            </li>
            <li
              className="remove-action-item"
              onClick={() => this.setState({ isDeleting: true })}
            >
              <FiTrash2 size={14} color="#FFFFFF" fill={"#FFFFFF"} />
              <p className="action-label"> Remove account</p>
            </li>
          </ul>
        </Popover.Body>
      </Popover>
    );

    return (
      <>
        <Card>
          <Card.Body>
            <div className="bank-info">
              <div>
                <p className="linked-bank">Linked Bank</p>

                <div className="description">
                  <div className="bank-details">
                    {/* accountType is Maybe<string> off the lodash `get`; lodash
                        capitalize coerces null/undefined to "" at runtime, so the
                        cast is type-only — behavior preserved. */}
                    <p className="checking">
                      {" "}
                      {capitalize(accountType as string | undefined)}
                    </p>
                    <span>
                      <BsDot
                        color="#0a2540"
                        size={28}
                        style={{ position: "relative", bottom: 1 }}
                      />
                      <BsDot
                        color="#0a2540"
                        size={28}
                        style={{ position: "relative", left: -15, bottom: 1 }}
                      />
                    </span>
                    <p
                      className="checking"
                      style={{ position: "relative", left: -22 }}
                    >
                      {accountId}
                    </p>
                  </div>
                </div>

                <Row>
                  <Col xs={4} className="bank-box-1">
                    <p className="bank-name">Account Number</p>
                  </Col>
                  <Col xs={6} className="bank-box">
                    <p className="bank-name">{accountId} </p>
                  </Col>
                </Row>

                <Row>
                  <Col xs={4} className="bank-box-1">
                    <p className="bank-name">Account Type</p>
                  </Col>
                  <Col xs={6} className="bank-box">
                    <p className="bank-name">{bankAlias} </p>
                  </Col>
                </Row>

                <Row>
                  <Col xs={4} className="bank-box-1">
                    <p className="bank-name">Bank</p>
                  </Col>
                  <Col xs={6} className="bank-box">
                    <p className="bank-name">{bankName}</p>
                  </Col>
                </Row>
              </div>
            </div>
          </Card.Body>
          <Card.Footer>
            <OverlayTrigger
              rootClose
              trigger="click"
              placement="bottom"
              overlay={popover}
            >
              <Button
                name="action"
                size="sm"
                color="action"
                btnLabel=" Actions"
              />
            </OverlayTrigger>
          </Card.Footer>
        </Card>
      </>
    );
  }

  _confirmDeleteDisplay() {
    const header = "Are you sure you want to remove your bank account?";
    return (
      <>
        <Card>
          <Card.Body>
            <div style={{ display: "flex", justifyContent: "center" }}>
              <IconCircle
                type="error"
                icon={
                  <FiAlertTriangle color="white" stroke="#B12121" size={16} />
                }
              />
            </div>
            <IconHeader variant="centeredText" headerText={header} />

            {!isEmpty(this.state.error) && (
              <Alert type="error" msg={this._getErrorMessage()} />
            )}

            <div className="remove-bank-action">
              <div className="bank-cancel">
                <Button
                  size="sm"
                  color="cancel"
                  name="cancel"
                  btnLabel="Cancel"
                  onClick={this._exitDeleteView}
                />
              </div>
              <Button
                size="sm"
                color="red"
                name="submit"
                btnLabel="Remove"
                onClick={this._removeBankLink}
                withArrow={true}
                loading={this.state.isLoading}
              />
            </div>
          </Card.Body>
        </Card>
      </>
    );
  }

  render() {
    let content;

    if (this.state.isDeleting) {
      content = this._confirmDeleteDisplay();
    } else {
      content = this._getDisplayView();
    }
    return (
      <>
        <SimpleModal
          show={this.state.showApprovedModal}
          onClose={() =>
            this.setState({ showApprovedModal: false, isDeleting: false })
          }
          onSubmit={this._confirmApprovedPayrollRemoval}
          title="Payroll Contributions are Approved"
          buttonText="Continue"
        >
          <p>
            {"There are currently payroll contributions approved for processing. By continuing and removing this bank," +
              " the payroll contributions will be un-approved. You will need to re-approve them after adding a new bank" +
              " and associating it to the Icon Plan. Would you like to continue?"}
          </p>
        </SimpleModal>
        <SimpleModal
          show={this.state.showProcessingModal}
          onClose={() =>
            this.setState({ showProcessingModal: false, isDeleting: false })
          }
          title="Payroll Contributions are Processing"
          buttonText="Continue"
        >
          <p>
            {"There are currently payroll contributions processing and you are unable to remove the bank account." +
              " Come back in 1 business day, after contributions have processed, to remove the bank."}
          </p>
        </SimpleModal>
        {content}
      </>
    );
  }
}

export default connector(withApollo<Props>(BankCard));
